import { memo, useRef, useMemo, useCallback, useEffect, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Crud,
  CrudFormComponent,
  CrudMode,
  CrudProps,
  notification,
  rejectionError,
  SelectsOptions,
  getSelectsOptions,
  transformCrudFormErrors,
  ValidationError,
  RenderFormItem,
} from '@ohpen/enso-ui';
import {
  FeeProfile,
  FeeProfileCrudRequest,
  UsageResponse,
  FeeDefinition,
  FeeProfilesCreateChangeRequestResponse,
  ChangeRequestOfFeeProfileCrudRequest,
  feeProfilesCheckUsage,
  feeProfilesCreateChangeRequest,
} from '@pmp/api';
import { crudToActionMap, getApiErrorMessage, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';

import { getFeeProfilesFormSchema } from '../formConfig';
import { Alert } from 'antd';
import { CrudFormProps } from '@pmp/types';
import { useFeatureToggle } from '@pmp/hooks';

export const FeeProfilesCrudForm = memo(({ mode, record, valueLists, refreshTable, onClose }: CrudFormProps<FeeProfile>): ReactElement => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(false);
  const [isUsed, setIsUsed] = useState<boolean | null>(null);
  const { t } = useTranslation();
  const enableFourEyes = useFeatureToggle(['enableFourEyes']);

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (isUsed !== null) return;
    if (mode !== CrudMode.EDIT) {
      setIsUsed(false);
      setOpen(true);
      return;
    }
    const { feeProfileId } = record || {};

    if (!feeProfileId) {
      notification().error({ title: t('global.errors.unexpected') });
      handleOnClose();
      return;
    }
    feeProfilesCheckUsage({ path: { feeProfileId } })
      .then(({ data: { isUsed } }: { data: UsageResponse }) => {
        setIsUsed(isUsed);
        setOpen(true);
      })
      .catch(error => {
        notification().error({ title: getApiErrorMessage(error) });
        handleOnClose();
      });
  }, [mode, record, isUsed, t, handleOnClose]);

  const onConfirm: (formData: FeeProfileCrudRequest) => Promise<ValidationError[] | void> = useCallback(
    data => {
      if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError(t('global.errors.invalidData')));
      const body: ChangeRequestOfFeeProfileCrudRequest = {
        data,
        actionType: crudToActionMap[mode],
      };

      return feeProfilesCreateChangeRequest({ body }).then(
        ({ data: { isValid, errors } }: { data: FeeProfilesCreateChangeRequestResponse }) => {
          if (!isValid) {
            const form = ref.current?.form;
            if (form) {
              const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
              if (errorFields.length) form.setFields(errorFields);
            }

            const allErrors = transformCrudFormErrors({ ...errors });
            return allErrors;
          }
          if (mode === CrudMode.EDIT || !enableFourEyes) refreshTable();
          notification().success({
            title: changeRequestNotificationMessage(t, enableFourEyes),
          });
        },
      );
    },
    [mode, refreshTable, t, enableFourEyes],
  );

  const renderAlert: RenderFormItem<FeeDefinition> = useCallback(
    (_schema, _config, _form) => {
      if (mode === CrudMode.EDIT && isUsed) return <Alert message={t('pmp.fees.labels.feeProfileInUseMessage')} type="warning" showIcon />;
      return null;
    },
    [mode, isUsed, t],
  );

  const title = useMemo(() => {
    if (!mode) return '';
    const titles: { [key: string]: string } = {
      [CrudMode.VIEW]: t('global.labels.viewX', { x: t('pmp.fees.labels.feeProfile') }),
      [CrudMode.CREATE]: t('global.labels.createX', { x: t('pmp.fees.labels.feeProfile') }),
      [CrudMode.EDIT]: t('global.labels.editX', { x: t('pmp.fees.labels.feeProfile') }),
    };
    return titles[mode];
  }, [mode, t]);

  const crudProps: CrudProps<FeeProfileCrudRequest> = useMemo(() => {
    const selectsOptions: SelectsOptions = (valueLists && getSelectsOptions(valueLists)) || {};

    return {
      ref,
      open,
      schema: getFeeProfilesFormSchema(selectsOptions, renderAlert, mode, isUsed),
      title,
      labels: {
        confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
      },
      initialValues: record as FeeProfileCrudRequest,
      mode,
      onClose: handleOnClose,
      onConfirm,
    };
  }, [valueLists, open, renderAlert, mode, isUsed, title, t, record, handleOnClose, onConfirm, enableFourEyes]);

  return <Crud {...crudProps} />;
});
